import { createTheme, alpha, PaletteMode, Shadows } from '@mui/material/styles';

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}
declare module '@mui/material/styles' {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange {}

  interface Palette {
    baseShadow: string;
  }
}

const defaultTheme = createTheme();

const customShadows: Shadows = [...defaultTheme.shadows];

// OpenIntake Brand Colors
// B5683C - Rust Orange (primary)
// 3E301E - Deep Brown (text)
// FDFCF9 - Soft Ivory (background)
// F7F3EB - Light Tan (secondary bg)
// FCDDA7 - Light Apricot
// C4B99D - Warm Beige
export const brand = {
  50: '#FDFCF9',   // Soft Ivory
  100: '#F7F3EB',  // Light Tan
  200: '#FCDDA7',  // Light Apricot
  300: '#C4B99D',  // Warm Beige
  400: '#B5683C',  // Rust Orange (primary)
  500: '#A05A32',  // Darker rust
  600: '#B5683C',  // Rust Orange
  700: '#8B4A28',  // Dark rust
  800: '#3E301E',  // Deep Brown
  900: '#2A2015',  // Darker brown
};

// Neutral tones based on brand
export const gray = {
  50: '#FDFCF9',   // Soft Ivory
  100: '#F7F3EB',  // Light Tan
  200: '#E8E2D8',  // Light warm gray
  300: '#C4B99D',  // Warm Beige
  400: '#9A8F7A',  // Medium warm gray
  500: '#6B5F4D',  // Dark warm gray
  600: '#4D4339',  // Darker warm gray
  700: '#3E301E',  // Deep Brown
  800: '#2A2015',  // Very dark brown
  900: '#1A150D',  // Near black
};

export const green = {
  50: 'hsl(120, 80%, 98%)',
  100: 'hsl(120, 75%, 94%)',
  200: 'hsl(120, 75%, 87%)',
  300: 'hsl(120, 61%, 77%)',
  400: 'hsl(120, 44%, 53%)',
  500: 'hsl(120, 59%, 30%)',
  600: 'hsl(120, 70%, 25%)',
  700: 'hsl(120, 75%, 16%)',
  800: 'hsl(120, 84%, 10%)',
  900: 'hsl(120, 87%, 6%)',
};

export const orange = {
  50: 'hsl(45, 100%, 97%)',
  100: 'hsl(45, 92%, 90%)',
  200: 'hsl(45, 94%, 80%)',
  300: 'hsl(45, 90%, 65%)',
  400: 'hsl(45, 90%, 40%)',
  500: 'hsl(45, 90%, 35%)',
  600: 'hsl(45, 91%, 25%)',
  700: 'hsl(45, 94%, 20%)',
  800: 'hsl(45, 95%, 16%)',
  900: 'hsl(45, 93%, 12%)',
};

export const red = {
  50: 'hsl(0, 100%, 97%)',
  100: 'hsl(0, 92%, 90%)',
  200: 'hsl(0, 94%, 80%)',
  300: 'hsl(0, 90%, 65%)',
  400: 'hsl(0, 90%, 40%)',
  500: 'hsl(0, 90%, 30%)',
  600: 'hsl(0, 91%, 25%)',
  700: 'hsl(0, 94%, 18%)',
  800: 'hsl(0, 95%, 12%)',
  900: 'hsl(0, 93%, 6%)',
};

export const getDesignTokens = (mode: PaletteMode) => {
  customShadows[1] =
    mode === 'dark'
      ? 'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px'
      : 'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px';

  return {
    palette: {
      mode,
      primary: {
        light: brand[200],
        main: brand[400],
        dark: brand[700],
        contrastText: brand[50],
        ...(mode === 'dark' && {
          contrastText: brand[50],
          light: brand[300],
          main: brand[400],
          dark: brand[700],
        }),
      },
      info: {
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[50],
        ...(mode === 'dark' && {
          contrastText: brand[300],
          light: brand[500],
          main: brand[700],
          dark: brand[900],
        }),
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
        ...(mode === 'dark' && {
          light: orange[400],
          main: orange[500],
          dark: orange[700],
        }),
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
        ...(mode === 'dark' && {
          light: red[400],
          main: red[500],
          dark: red[700],
        }),
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
        ...(mode === 'dark' && {
          light: green[400],
          main: green[500],
          dark: green[700],
        }),
      },
      grey: {
        ...gray,
      },
      divider: mode === 'dark' ? alpha(gray[700], 0.6) : alpha(gray[300], 0.4),
      background: {
        default: '#FDFCF9',  // Soft Ivory
        paper: '#FFFFFF',     // White for cards
        ...(mode === 'dark' && { default: gray[900], paper: '#1A150D' }),
      },
      text: {
        primary: '#3E301E',   // Deep Brown
        secondary: '#6B5F4D', // Dark warm gray
        warning: orange[400],
        ...(mode === 'dark' && { primary: '#FDFCF9', secondary: gray[400] }),
      },
      action: {
        hover: alpha(gray[200], 0.2),
        selected: `${alpha(gray[200], 0.3)}`,
        ...(mode === 'dark' && {
          hover: alpha(gray[600], 0.2),
          selected: alpha(gray[600], 0.3),
        }),
      },
    },
    typography: {
      fontFamily: '"Manrope", -apple-system, BlinkMacSystemFont, sans-serif',
      h1: {
        fontFamily: '"Manrope", sans-serif',
        fontSize: defaultTheme.typography.pxToRem(56),
        fontWeight: 400,
        lineHeight: 1.15,
        letterSpacing: -0.5,
      },
      h2: {
        fontFamily: '"Manrope", sans-serif',
        fontSize: defaultTheme.typography.pxToRem(42),
        fontWeight: 400,
        lineHeight: 1.2,
      },
      h3: {
        fontFamily: '"Manrope", sans-serif',
        fontSize: defaultTheme.typography.pxToRem(32),
        fontWeight: 400,
        lineHeight: 1.25,
      },
      h4: {
        fontFamily: '"Manrope", sans-serif',
        fontSize: defaultTheme.typography.pxToRem(28),
        fontWeight: 400,
        lineHeight: 1.3,
      },
      h5: {
        fontFamily: '"Manrope", sans-serif',
        fontSize: defaultTheme.typography.pxToRem(20),
        fontWeight: 800,
      },
      h6: {
        fontFamily: '"Manrope", sans-serif',
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 600,
      },
      subtitle1: {
        fontFamily: '"Manrope", sans-serif',
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 400,
      },
      subtitle2: {
        fontFamily: '"Manrope", sans-serif',
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 500,
      },
      body1: {
        fontFamily: '"Manrope", sans-serif',
        fontSize: defaultTheme.typography.pxToRem(16),
        fontWeight: 400,
        lineHeight: 1.7,
      },
      body2: {
        fontFamily: '"Manrope", sans-serif',
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 400,
        lineHeight: 1.6,
      },
      caption: {
        fontFamily: '"Manrope", sans-serif',
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: 400,
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: customShadows,
  };
};

export const colorSchemes = {
  light: {
    palette: {
      primary: {
        light: brand[200],
        main: brand[400],
        dark: brand[700],
        contrastText: brand[50],
      },
      info: {
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[50],
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[300], 0.4),
      background: {
        default: '#FDFCF9',  // Soft Ivory
        paper: '#FFFFFF',     // White for cards
      },
      text: {
        primary: '#3E301E',   // Deep Brown
        secondary: '#6B5F4D', // Dark warm gray
        warning: orange[400],
      },
      action: {
        hover: alpha(gray[200], 0.2),
        selected: `${alpha(gray[200], 0.3)}`,
      },
      baseShadow:
        'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: brand[50],
        light: brand[300],
        main: brand[400],
        dark: brand[700],
      },
      info: {
        contrastText: brand[300],
        light: brand[500],
        main: brand[700],
        dark: brand[900],
      },
      warning: {
        light: orange[400],
        main: orange[500],
        dark: orange[700],
      },
      error: {
        light: red[400],
        main: red[500],
        dark: red[700],
      },
      success: {
        light: green[400],
        main: green[500],
        dark: green[700],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[700], 0.6),
      background: {
        default: gray[900],
        paper: 'hsl(220, 30%, 7%)',
      },
      text: {
        primary: 'hsl(0, 0%, 100%)',
        secondary: gray[400],
      },
      action: {
        hover: alpha(gray[600], 0.2),
        selected: alpha(gray[600], 0.3),
      },
      baseShadow:
        'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
    },
  },
};

// Typography using Manrope (body/headings) and Instrument Serif (accents)
export const typography = {
  fontFamily: '"Manrope", -apple-system, BlinkMacSystemFont, sans-serif',
  h1: {
    fontFamily: '"Manrope", sans-serif',
    fontSize: defaultTheme.typography.pxToRem(56),
    fontWeight: 400, // Regular
    lineHeight: 1.15,
    letterSpacing: -0.5,
  },
  h2: {
    fontFamily: '"Manrope", sans-serif',
    fontSize: defaultTheme.typography.pxToRem(42),
    fontWeight: 400, // Regular
    lineHeight: 1.2,
  },
  h3: {
    fontFamily: '"Manrope", sans-serif',
    fontSize: defaultTheme.typography.pxToRem(32),
    fontWeight: 400, // Regular
    lineHeight: 1.25,
  },
  h4: {
    fontFamily: '"Manrope", sans-serif',
    fontSize: defaultTheme.typography.pxToRem(28),
    fontWeight: 400, // Regular
    lineHeight: 1.3,
  },
  h5: {
    fontFamily: '"Manrope", sans-serif',
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 800, // Extra Bold
  },
  h6: {
    fontFamily: '"Manrope", sans-serif',
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
  },
  subtitle1: {
    fontFamily: '"Manrope", sans-serif',
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 400,
  },
  subtitle2: {
    fontFamily: '"Manrope", sans-serif',
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
  },
  body1: {
    fontFamily: '"Manrope", sans-serif',
    fontSize: defaultTheme.typography.pxToRem(16),
    fontWeight: 400, // Regular
    lineHeight: 1.7,
  },
  body2: {
    fontFamily: '"Manrope", sans-serif',
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
    lineHeight: 1.6,
  },
  caption: {
    fontFamily: '"Manrope", sans-serif',
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
  },
};

export const shape = {
  borderRadius: 8,
};

// @ts-ignore
const defaultShadows: Shadows = [
  'none',
  'var(--template-palette-baseShadow)',
  ...defaultTheme.shadows.slice(2),
];
export const shadows = defaultShadows;
